/* BookCover — renders a cookbook's "front cover" with title + optional subtitle overlaid
 * on the cover image (or palette gradient fallback). Used in three places:
 *   1. CookbookView  — large 4:5 hero
 *   2. CookbookEdit  — live preview while editing
 *   3. Dashboard     — small tile in the cookbook grid
 *
 * Sizes are controlled via the `size` prop (sm | md | lg).
 *
 * Design intent: actually LOOK like a printed cookbook — serif display title, thin gold
 * "Recipe Filer" eyebrow at top, subtitle in italics under the title, gradient at the
 * bottom of any cover photo so the text always sits legibly on it.
 */
import { BookOpenText } from "@phosphor-icons/react";

const SIZE_STYLES = {
  sm: {
    container: "aspect-[4/5] rounded-2xl",
    pad: "p-4",
    eyebrow: "text-[8px] tracking-[0.25em]",
    title: "text-lg sm:text-xl leading-[1.05]",
    subtitle: "text-[10px] sm:text-xs mt-1.5",
    icon: 28,
  },
  md: {
    container: "aspect-[4/5] rounded-3xl",
    pad: "p-6",
    eyebrow: "text-[10px] tracking-[0.3em]",
    title: "text-3xl md:text-4xl leading-[1.05]",
    subtitle: "text-sm md:text-base mt-2",
    icon: 48,
  },
  lg: {
    container: "aspect-[4/5] rounded-3xl",
    pad: "p-10",
    eyebrow: "text-xs tracking-[0.3em]",
    title: "text-4xl md:text-5xl lg:text-6xl leading-[1.0]",
    subtitle: "text-base md:text-lg mt-3",
    icon: 64,
  },
};

export default function BookCover({ cookbook, size = "md", className = "", "data-testid": testId }) {
  const cb = cookbook || {};
  const s = SIZE_STYLES[size] || SIZE_STYLES.md;
  const hasImage = Boolean(cb.cover_image_url);
  const showTitleText = cb.show_title_on_cover !== false; // default to true for legacy cookbooks
  const title = cb.title || "Untitled cookbook";
  const subtitle = cb.subtitle || "";
  // User-controlled cover title scale (sm | md | lg | xl) — bumps the title
  // block's `transform: scale()` so the lettering grows / shrinks on the
  // physical cover without changing the title's position.
  const titleScale = {
    sm: 0.78, md: 1, lg: 1.18, xl: 1.36,
  }[cb.cover_title_size || "md"] || 1;

  const gradient = `linear-gradient(135deg, ${cb.palette_primary || "#FFAE3D"}, ${cb.palette_secondary || "#EF8200"})`;

  return (
    <div
      className={`relative overflow-hidden shadow-lg ${s.container} ${className}`}
      style={{
        background: gradient,
        fontFamily: cb.font_choice || "inherit",
      }}
      data-testid={testId || "book-cover"}
    >
      {/* Background: cover image OR gradient with book icon */}
      {hasImage ? (
        <>
          <img
            src={cb.cover_image_url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            aria-hidden="true"
          />
          {/* Dark gradient at the bottom so the text is always legible regardless of image.
              Skipped entirely when the user has chosen to hide the text overlay (pre-designed
              covers with the title already printed look best with NO overlay). */}
          {showTitleText && (
            <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/30 to-black/75" aria-hidden="true" />
          )}
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center opacity-25" aria-hidden="true">
          <BookOpenText size={s.icon * 2} weight="duotone" className="text-rf-ink" />
        </div>
      )}

      {/* Overlay text — title block only (the "A Recipe Filer cookbook"
          eyebrow was removed per user request 2026-02-23 — admins now
          consider it visual clutter on the published cover). */}
      {showTitleText && (
        <div className={`relative z-10 w-full h-full flex flex-col justify-end ${s.pad} text-center ${hasImage ? "text-white" : "text-rf-ink"}`}>
          <div className="flex flex-col items-center justify-end">
            {cb.logo_url && (
              <img
                src={cb.logo_url}
                alt=""
                className="w-12 h-12 sm:w-14 sm:h-14 object-contain mb-3 rounded-xl bg-white/80 p-2"
                aria-hidden="true"
              />
            )}
            <h2
              className={`font-display font-black ${s.title}`}
              style={{
                fontFamily: cb.font_choice || "inherit",
                textShadow: hasImage ? "0 2px 10px rgba(0,0,0,0.45)" : "none",
                transform: `scale(${titleScale})`,
                transformOrigin: "center bottom",
              }}
              data-testid="book-cover-title"
            >
              {title}
            </h2>
            {subtitle && (
              <p
                className={`font-medium italic ${s.subtitle} ${hasImage ? "text-white/90" : "text-rf-ink/70"}`}
                style={{
                  fontFamily: cb.font_choice || "inherit",
                  textShadow: hasImage ? "0 1px 6px rgba(0,0,0,0.55)" : "none",
                }}
                data-testid="book-cover-subtitle"
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
